import { useCallback, useEffect, useRef, useState } from "react";
import pagosService from "../../../../services/pagosService";
import pagoEmpresaService from "../../../../services/pagoEmpresaService";
import { normalizePago } from "../adapters/pagosAdapter";
import { initialPagoFilters } from "../constants";
import { showToast } from "../../../../utils/showToast";
import { getSocket, initSocket } from "../../../../utils/socketClient";

export default function usePagosList() {
  const [pagos, setPagos] = useState([]);
  const [pagosEmpresariales, setPagosEmpresariales] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialPagoFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState({
    total: 0,
    totalAnulados: 0,
    pendientes: 0,
    parciales: 0,
    pagados: 0,
    montoTotal: 0,
    montoHoy: 0,
    montoMes: 0,
  });

  const fetchResumen = useCallback(async () => {
    try {
      const srv = await pagosService.resumen();
      setResumen({
        total: Number(srv?.total || 0),
        totalAnulados: Number(srv?.totalAnulados || 0),
        pendientes: Number(srv?.pendientes || 0),
        parciales: Number(srv?.parciales || 0),
        pagados: Number(srv?.pagados || 0),
        montoTotal: Number(srv?.montoTotal || 0),
        montoHoy: Number(srv?.montoHoy || 0),
        montoMes: Number(srv?.montoMes || 0),
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchPagos = useCallback(async (pageOverride, filtrosOverride) => {
    setLoading(true);
    const currentPage = pageOverride ?? page;
    const currentFiltros = filtrosOverride ?? filtros;
    try {
      const params = {
        pagina: currentPage,
        porPagina: pageSize,
        search: currentFiltros.search || undefined,
        estado: currentFiltros.estado_pago !== 'all' ? currentFiltros.estado_pago : undefined,
        metodo_pago: currentFiltros.metodo_pago !== 'all' ? currentFiltros.metodo_pago : undefined,
        tipo_pago: currentFiltros.tipo_pago !== 'all' ? currentFiltros.tipo_pago : undefined,
        comprobante: currentFiltros.comprobante !== 'all' ? currentFiltros.comprobante : undefined,
        fecha_desde: currentFiltros.fecha_desde || undefined,
        fecha_hasta: currentFiltros.fecha_hasta || undefined,
      };

      const response = await pagosService.listar(params);
      const parsed = (Array.isArray(response?.data) ? response.data : []).map(normalizePago);
      setPagos(parsed);
      setTotal(Number(response?.total || 0));

      try {
        const empresariales = await pagoEmpresaService.listar();
        const normalizados = (Array.isArray(empresariales) ? empresariales : []).map((pe) => ({
          id: `emp-${pe.id}`,
          _pagoEmpresaId: pe.id,
          tipo: "empresarial",
          estado_empresa: pe.estado,
          fechaPago: pe.fecha_pago,
          monto: Number(pe.monto_total || 0),
          metodoPago: pe.metodo_pago || "",
          referencia: pe.referencia_externa || "",
          estudiante: pe.empresa_nombre || "Empresa",
          empresa_nombre: pe.empresa_nombre,
          empresa_ruc: pe.empresa_ruc,
          codigoMatricula: null,
          estadoPago: pe.estado === "por_cobrar" ? "por_cobrar" : Number(pe.saldo_favor || 0) > 0 ? "parcial" : "pagado",
          estadoMatricula: null,
          afectaMatricula: true,
          concepto: "Empresarial",
          saldo_favor: pe.estado === "por_cobrar" ? 0 : Number(pe.saldo_favor || 0),
          total_pagos: Number(pe.total_pagos || 0),
          createdAt: pe.fecha_pago,
        }));
        setPagosEmpresariales(normalizados);
      } catch {
        // empresariales no críticos
      }

      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo obtener los pagos.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filtros]);

  useEffect(() => {
    fetchPagos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // Los totales KPI no dependen de la pagina/filtro actual, asi que se piden
  // una sola vez al montar (y luego solo cuando algo realmente cambia via
  // socket o al crear un pago), no en cada click de paginacion.
  useEffect(() => {
    fetchResumen();
  }, [fetchResumen]);

  const prevFiltrosRef = useRef(filtros);
  useEffect(() => {
    if (prevFiltrosRef.current === filtros) return;
    prevFiltrosRef.current = filtros;
    setPage(1);
    fetchPagos(1, filtros);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  const refreshTimeoutRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) return;
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      fetchPagos();
      fetchResumen();
    }, 350);
  }, [fetchPagos, fetchResumen]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const socket = getSocket() || initSocket(token);
    if (!socket) return undefined;

    socket.on("pagos:actualizados", scheduleRefresh);
    socket.on("matriculas:actualizadas", scheduleRefresh);

    return () => {
      socket.off("pagos:actualizados", scheduleRefresh);
      socket.off("matriculas:actualizadas", scheduleRefresh);
    };
  }, [scheduleRefresh]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const pagosPaginados = [...pagos, ...pagosEmpresariales].sort((a, b) => {
    const da = new Date(b.fechaPago || b.createdAt || 0);
    const db = new Date(a.fechaPago || a.createdAt || 0);
    return da - db;
  });

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const seleccionar = useCallback(async (pago) => {
    if (!pago) {
      setSeleccionado(null);
      return;
    }
    const id = pago.id || pago;
    try {
      const detalle = await pagosService.obtener(id);
      const normalized = normalizePago(detalle);
      if (normalized && !normalized.codigoMatricula) {
        const fromList = pagos.find((item) => item.id === normalized.id);
        if (fromList?.codigoMatricula) {
          normalized.codigoMatricula = fromList.codigoMatricula;
        }
      }
      setSeleccionado(normalized);
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: "No se pudo cargar el pago." });
    }
  }, [pagos]);

  const limpiarFiltros = useCallback(() => setFiltros(initialPagoFilters), []);

  const reload = useCallback(
    async (...args) => {
      await Promise.all([fetchPagos(...args), fetchResumen()]);
    },
    [fetchPagos, fetchResumen]
  );

  return {
    pagos,
    pagosFiltrados: pagos,
    seleccionado,
    setSeleccionado,
    loading,
    error,
    resumen,
    filtros,
    setFiltros,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    pagosPaginados,
    reload,
    seleccionar,
  };
}
